import { isAxiosError } from 'axios';
import apiClient from '../client';
import { getCurrentUserUid } from '../../firebase/auth';
import type { OwnProfile } from './profileRules';

// The signed-in person's own profile (27 Oct, the sign-in build, steps 5 and 6): PUT /account/profile on the gateway,
// which saves it for the uid in the verified token. No role is needed, so it works straight after sign-up, before the
// email is confirmed. The company is optional; the names are required (the gateway checks both).

// The profile's shape and the field rules live in profileRules.ts (no network, no Firebase), re-exported here.
export { cleanProfileText, profileFieldInvalid, PROFILE_FIELD_MAX } from './profileRules';
export type { OwnProfile } from './profileRules';

export const ACCOUNT_PROFILE_PATH = '/account/profile';

/** The caller's own saved profile (GET /account/profile, item 5), or null when they never saved one. */
export const getOwnProfile = async (): Promise<OwnProfile | null> => {
  const { data } = await apiClient.get<{ profile: (OwnProfile & { updatedAt?: string }) | null }>(ACCOUNT_PROFILE_PATH);
  const profile = data?.profile;
  if (!profile) return null;
  return {
    firstName: typeof profile.firstName === 'string' ? profile.firstName : '',
    lastName: typeof profile.lastName === 'string' ? profile.lastName : '',
    companyName: typeof profile.companyName === 'string' ? profile.companyName : '',
  };
};

/** One save from "My information" (item 5): the gateway's answer is the saved profile. Errors go to the caller. */
export const putOwnProfile = async (profile: OwnProfile): Promise<OwnProfile> => {
  const { data } = await apiClient.put<OwnProfile>(ACCOUNT_PROFILE_PATH, profile);
  return {
    firstName: typeof data?.firstName === 'string' ? data.firstName : profile.firstName,
    lastName: typeof data?.lastName === 'string' ? data.lastName : profile.lastName,
    companyName: typeof data?.companyName === 'string' ? data.companyName : profile.companyName,
  };
};

// Three tries, waiting a little longer each time. Only a network failure or a 5xx is tried again; a 4xx (a refused
// field, a missing sign-in) will not change by retrying.
const RETRY_DELAYS_MS = [0, 800, 2000];

const shouldRetry = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || status >= 500;
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// The profile typed at sign-up that could not be saved, kept IN MEMORY ONLY (never localStorage: it is personal data),
// so the waiting page can offer "Save my details" (step 7). A reload forgets it; the grant then falls back to the
// display name sign-up also set. It belongs to the account that typed it: it is kept with that account's uid, handed
// back only while the same account is signed in, and cleared on sign-out, so another account signing in in the same
// tab can never be offered, or save, someone else's names.
let unsaved: { uid: string; profile: OwnProfile } | null = null;

export const getUnsavedProfile = (): OwnProfile | null => {
  const uid = getCurrentUserUid();
  return unsaved && uid && unsaved.uid === uid ? unsaved.profile : null;
};

// Told whenever the kept profile appears or goes (27 Oct, step 7): sign-up moves on to the waiting page before its
// three tries are over, so the page listens rather than reading once when it opens.
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

/** Call `listener` whenever the kept profile changes; returns the unsubscribe. */
export const onUnsavedProfileChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const clearUnsavedProfile = (): void => {
  unsaved = null;
  changed();
};

/** Save the caller's own profile, with the retries above. Throws the last error when every try failed. */
export async function saveOwnProfile(profile: OwnProfile): Promise<void> {
  let lastError: unknown;
  for (const delay of RETRY_DELAYS_MS) {
    if (delay) await wait(delay);
    try {
      await apiClient.put(ACCOUNT_PROFILE_PATH, profile);
      if (unsaved) {
        unsaved = null;
        changed();
      }
      return;
    } catch (error) {
      lastError = error;
      if (!shouldRetry(error)) break;
    }
  }
  const uid = getCurrentUserUid();
  unsaved = uid ? { uid, profile } : null;
  changed();
  throw lastError;
}
